import { useState } from "react";
import type { ComponentType } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import {
  Boxes,
  ChevronsUpDown,
  Check,
  Cloud,
  LayoutDashboard,
  LogOut,
  Menu,
  Plug,
  Settings,
  Sparkles,
  Store,
  Workflow,
  X,
} from "lucide-react";
import { Logo, LogoMark } from "../Logo";
import { AppFooter } from "./Footer";
import { Spinner } from "../ui/Spinner";
import { useAuth } from "../../lib/auth";
import { cn } from "../../lib/cn";

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/connections", label: "Connections", icon: Plug },
  { to: "/app/exchange", label: "Exchange", icon: Store },
  { to: "/app/workflows", label: "Workflows", icon: Workflow },
  { to: "/app/apis", label: "API Designer", icon: Cloud },
  { to: "/app/copilot", label: "Copilot", icon: Sparkles },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

function initials(name: string | null, email: string): string {
  const src = (name && name.trim()) || email;
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="flex flex-1 flex-col gap-1 px-3">
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
              isActive
                ? "bg-brand-50 text-brand-800"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon className={cn("h-[18px] w-[18px]", isActive ? "text-brand-600" : "text-slate-400 group-hover:text-slate-600")} />
              {label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function TenantSwitcher() {
  const { tenants, currentTenant, selectTenant } = useAuth();
  const [open, setOpen] = useState(false);
  if (!currentTenant) return null;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="focus-ring flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-gradient text-[11px] font-bold text-white">
          {currentTenant.display_name[0]?.toUpperCase()}
        </span>
        <span className="max-w-[10rem] truncate">{currentTenant.display_name}</span>
        <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute left-0 z-20 mt-2 w-64 animate-scale-in rounded-xl border border-slate-200 bg-white p-1.5 shadow-lift">
            <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Workspaces</p>
            {tenants.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  selectTenant(t.slug);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-gradient text-[11px] font-bold text-white">
                  {t.display_name[0]?.toUpperCase()}
                </span>
                <span className="flex-1 truncate">
                  <span className="block font-medium text-slate-800">{t.display_name}</span>
                  <span className="block text-xs capitalize text-slate-400">{t.role}</span>
                </span>
                {t.slug === currentTenant.slug && <Check className="h-4 w-4 text-brand-600" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="focus-ring flex h-9 w-9 items-center justify-center rounded-full bg-slate-800 text-sm font-semibold text-white ring-2 ring-white transition hover:ring-slate-200"
      >
        {initials(user.full_name, user.email)}
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-20 mt-2 w-60 animate-scale-in rounded-xl border border-slate-200 bg-white p-1.5 shadow-lift">
            <div className="border-b border-slate-100 px-3 py-2.5">
              <p className="truncate text-sm font-semibold text-slate-800">{user.full_name || "Account"}</p>
              <p className="truncate text-xs text-slate-400">{user.email}</p>
            </div>
            <Link
              to="/app/settings"
              onClick={() => setOpen(false)}
              className="mt-1 flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
            >
              <Settings className="h-4 w-4 text-slate-400" /> Settings
            </Link>
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function AppLayout() {
  const { user, loading, currentTenant } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Spinner className="h-8 w-8 text-brand-500" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Signed in through B2C but not in any workspace yet — onboard first.
  if (!currentTenant) return <Navigate to="/onboarding" replace />;

  // Column layout so the footer is pushed to the bottom of the viewport on
  // short pages instead of floating directly under the content.
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 lg:pl-64">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="flex h-16 items-center px-5">
          <Link to="/app" className="focus-ring rounded-lg">
            <Logo subtitle="Wise 2.0" size={30} />
          </Link>
        </div>
        <SidebarNav />
        <div className="p-3">
          <div className="rounded-xl bg-brand-gradient p-4 text-white">
            <Sparkles className="h-5 w-5" />
            <p className="mt-2 text-sm font-semibold">Build with a prompt</p>
            <p className="mt-1 text-xs text-white/80">Describe a system in plain English and let AI wire it up.</p>
            <Link
              to="/app/connections/new"
              className="mt-3 inline-flex rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold backdrop-blur hover:bg-white/25"
            >
              New integration →
            </Link>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 animate-slide-up flex-col border-r border-slate-200 bg-white">
            <div className="flex h-16 items-center justify-between px-5">
              <Logo subtitle="Wise 2.0" size={30} />
              <button onClick={() => setMobileOpen(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Topbar */}
      <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-xl sm:px-6">
        <button
          onClick={() => setMobileOpen(true)}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="lg:hidden">
          <LogoMark size={28} />
        </span>
        <TenantSwitcher />
        <div className="flex-1" />
        <Link
          to="/app/copilot"
          className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm text-slate-500 hover:bg-slate-50 sm:flex"
        >
          <Boxes className="h-4 w-4" /> Ask across systems…
        </Link>
        <UserMenu />
      </header>

      {/* Content */}
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        <Outlet />
      </main>
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <AppFooter />
      </div>
    </div>
  );
}
